// v17.5 — MP3 encoder for the offline render.
// Takes the SAME AudioBuffer that `audiobuffer-to-wav` receives from
// `engine.exportToWav`, so FX chain / sound design / arrangement parity
// with the WAV path is guaranteed — only the final encoding step differs.
//
// Uses @breezystack/lamejs (maintained fork of lamejs). Encoded at 192kbps
// stereo, matching the user's "high-quality" spec.
import lamejs from "@breezystack/lamejs";

const MP3_BITRATE_KBPS = 192;
// LAME expects each encode call to receive a chunk of 16-bit PCM samples.
// 1152 samples is the natural MP3 frame size (one MPEG-1 Layer 3 granule
// pair), which produces cleanest output.
const SAMPLE_BLOCK = 1152;

/**
 * Convert an AudioBuffer (typically the output of Tone.Offline) into an
 * MP3-encoded Blob.
 *
 * @param {AudioBuffer} audioBuffer
 * @returns {Blob} audio/mpeg blob suitable for URL.createObjectURL download.
 */
export function audioBufferToMp3(audioBuffer) {
  const channels = Math.min(2, audioBuffer.numberOfChannels || 1);
  const sampleRate = audioBuffer.sampleRate;
  const encoder = new lamejs.Mp3Encoder(channels, sampleRate, MP3_BITRATE_KBPS);

  // Float32 [-1..1] -> Int16 PCM. Clamp is required — Tone.Offline can
  // produce values marginally outside the range when heavy FX (Crow
  // Pumper, distortion) push the signal hot.
  const toInt16 = (float32) => {
    const out = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
      const s = Math.max(-1, Math.min(1, float32[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  };

  const left = toInt16(audioBuffer.getChannelData(0));
  const right = channels === 2
    ? toInt16(audioBuffer.getChannelData(1))
    : null;

  const mp3Chunks = [];
  for (let i = 0; i < left.length; i += SAMPLE_BLOCK) {
    const leftChunk = left.subarray(i, i + SAMPLE_BLOCK);
    const rightChunk = right ? right.subarray(i, i + SAMPLE_BLOCK) : null;
    const buf = channels === 2
      ? encoder.encodeBuffer(leftChunk, rightChunk)
      : encoder.encodeBuffer(leftChunk);
    if (buf.length > 0) mp3Chunks.push(buf);
  }
  const flush = encoder.flush();
  if (flush.length > 0) mp3Chunks.push(flush);

  return new Blob(mp3Chunks, { type: "audio/mpeg" });
}

export default audioBufferToMp3;
